import React from 'react';
import { Link } from 'react-router-dom';
import styles from './CharacterProfile.module.css';
import { infoIcon, historyIcon, visibilityPublicIcon, visibilityPrivateIcon, visibilityUnlistedIcon } from '../../assets/icons';
import ChatHistory from './ChatHistory';
import { CharacterDetailResponse, Visibility } from '../../api/types/chat';

interface VisibilityIconProps {
  visibility: Visibility;
  className?: string;
}

// Visibility icon mapping - extracted from CharacterProfile
const VisibilityIcon: React.FC<VisibilityIconProps> = ({ visibility, className }) => {
  const iconProps = { className };
  
  switch (visibility) {
    case 'public':
      return <img src={visibilityPublicIcon} alt="Public" {...iconProps} />;
    case 'private':
      return <img src={visibilityPrivateIcon} alt="Private" {...iconProps} />;
    case 'unlisted':
      return <img src={visibilityUnlistedIcon} alt="Unlisted" {...iconProps} />;
    default:
      return <img src={visibilityPublicIcon} alt="Public" {...iconProps} />;
  }
};

interface ChatType {
  id: string;
}

interface CharacterProfileTabsProps {
  activeTab: 'about' | 'history' | 'edit';
  setActiveTab: (tab: 'about' | 'history' | 'edit') => void;
  character: CharacterDetailResponse;
  isCurrentAuthor: boolean;
  isEditMode: boolean;
  openModal: (type: string, mode: 'edit' | 'view') => void;
  chat?: ChatType;
  characterVisibilityState: Visibility;
  handleVisibilityChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isUpdatingVisibility: boolean;
  visibilityError: string | null;
}

const CharacterProfileTabs: React.FC<CharacterProfileTabsProps> = ({ 
  activeTab, 
  setActiveTab, 
  character, 
  isCurrentAuthor, 
  isEditMode, 
  openModal, 
  chat,
  characterVisibilityState,
  handleVisibilityChange,
  isUpdatingVisibility,
  visibilityError
}) => {
  // Render the tab content based on the active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'about':
        return (
          <div className={styles.tabContent}>
            <div className={styles.aboutSection}>
              <div className={styles.aboutList}>
                <Link 
                  to={`/users/${character.author}`} 
                  state={chat ? { fromChatId: chat.id } : undefined}
                  className={`${styles.aboutListItem} ${styles.authorItem}`}
                >
                  <div className={styles.inlineLabel}>Author</div>
                  <div className={styles.authorUsername}>@{character.author}</div>
                </Link>

                <div 
                  className={styles.aboutListItem} 
                  onClick={() => openModal('description', isEditMode ? 'edit' : 'view')}
                >
                  <div className={styles.aboutListItemHeader}>
                    <div className={styles.aboutLabel}>Description</div>
                  </div>
                  <div className={styles.aboutValue}>
                    {character.description || "This character hasn't written their description yet."}
                  </div>
                </div>

                <div 
                  className={styles.aboutListItem}
                  onClick={() => openModal('external_description', isEditMode ? 'edit' : 'view')}
                >
                  <div className={styles.aboutListItemHeader}>
                    <div className={styles.aboutLabel}>External Description <span className={styles.helperText}>only used in scenarios</span></div>
                  </div>
                  <div className={styles.aboutValue}>
                    {character.external_description || "No external description available."}
                  </div>
                </div>

                <div 
                  className={styles.aboutListItem}
                  onClick={() => openModal('greeting', isEditMode ? 'edit' : 'view')}
                >
                  <div className={styles.aboutListItemHeader}>
                    <div className={styles.aboutLabel}>Greeting</div>
                  </div>
                  <div className={styles.aboutValue}>
                    {character.greeting || "Hey there! Nice to meet you! 👋"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'history':
        return (
          <div className={styles.tabContent}>
            <ChatHistory character={character} />
          </div>
        );
      case 'edit':
        return (
          <div className={styles.tabContent}>
            <div className={styles.aboutSection}>
              <div className={styles.aboutList}>
                <Link 
                  to={`/users/${character.author}`} 
                  state={chat ? { fromChatId: chat.id } : undefined}
                  className={`${styles.aboutListItem} ${styles.authorItem} ${styles.spacer}`}
                >
                  <div className={styles.inlineLabel}>Author</div>
                  <div className={styles.authorUsername}>@{character.author}</div>
                </Link>

                <div 
                  className={styles.aboutListItem}
                  onClick={() => {
                    openModal('description', isEditMode ? 'edit' : 'view');
                  }}
                >
                  <div className={styles.aboutListItemHeader}>
                    <div className={styles.aboutLabel}>Description</div>
                  </div>
                  <div className={styles.aboutValue}>
                    {character.description || "This character hasn't written their description yet."}
                  </div>
                </div>

                <div 
                  className={styles.aboutListItem}
                  onClick={() => {
                    openModal('external_description', isEditMode ? 'edit' : 'view');
                  }}
                >
                  <div className={styles.aboutListItemHeader}>
                    <div className={styles.aboutLabel}>External Description <span className={styles.helperText}>only used in scenarios</span></div>
                  </div>
                  <div className={styles.aboutValue}>
                    {character.external_description || "No external description available."}
                  </div>
                </div>

                <div 
                  className={styles.aboutListItem}
                  onClick={() => {
                    openModal('greeting', isEditMode ? 'edit' : 'view');
                  }}
                >
                  <div className={styles.aboutListItemHeader}>
                    <div className={styles.aboutLabel}>Greeting</div>
                  </div>
                  <div className={styles.aboutValue}>
                    {character.greeting || "Hey there! Nice to meet you! 👋"}
                  </div>
                </div>

                {isEditMode && (
                  <div className={styles.aboutListItem}>
                    <div className={styles.aboutListItemHeader}>
                      <div className={styles.aboutLabel}>Visibility</div>
                    </div>
                    <div className={styles.visibilitySelector}>
                      <div className={styles.visibilityWrapper}>
                        <span className={styles.visibilityIcon}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </span>
                        <select
                          value={characterVisibilityState}
                          onChange={handleVisibilityChange}
                          disabled={isUpdatingVisibility}
                          className={`${styles.visibilitySelect} ${isUpdatingVisibility ? styles.loading : ''}`}
                        >
                          <option value="public">Public</option>
                          <option value="unlisted">Unlisted</option>
                          <option value="private">Private</option>
                        </select>
                        {isUpdatingVisibility && (
                          <span className={styles.loadingIndicator}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.spinner}>
                              <circle cx="12" cy="12" r="10"></circle>
                              <path d="M12 6v6l4 2"></path>
                            </svg>
                          </span>
                        )}
                        {visibilityError && <div className={styles.error}>{visibilityError}</div>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className={styles.tabs}>
        {isCurrentAuthor && (
          <div className={styles.tabsVisibilityIndicator}>
            <VisibilityIcon 
              visibility={character.visibility || 'public'} 
              className={styles.tabsVisibilityIcon}
            />
          </div>
        )}
        <button 
          className={`${styles.tab} ${activeTab === 'about' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('about')}
          type="button"
        >
          <img src={infoIcon} alt="" className={styles.tabIcon} />
          About
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'history' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('history')}
          type="button"
        >
          <img src={historyIcon} alt="" className={styles.tabIcon} />
          Chats
        </button>
      </div>
      
      <div className={styles.tabContentWrapper}>
        {renderTabContent()}
      </div>
    </>
  );
};

export default CharacterProfileTabs; 